import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import './applications.css';
import { ApplicationCard } from './ApplicationCard.jsx';
import { ApplicationDetail } from './ApplicationDetail.jsx';
import { useDocumentTitle } from './lib/useDocumentTitle.js';

const GROUPING_LS_KEY = 'jobe.applications.grouping';
const SEARCH_DEBOUNCE_MS = 120;

const GROUP_BY_OPTS = [
  { k: 'owner',  l: 'Owner' },
  { k: 'source', l: 'Source kind' },
  { k: 'flat',   l: 'Flat' },
];
const UI_OPTS = [
  { k: '*', l: 'All' },
  { k: 'y', l: 'Yes' },
  { k: 'n', l: 'No' },
];

function useApplications() {
  const [apps, setApps] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/applications', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => alive && setApps(Array.isArray(j.applications) ? j.applications : []))
      .catch((e) => alive && setErr(String(e)));
    return () => { alive = false; };
  }, []);
  return { apps, err };
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function initialGrouping(fromUrl) {
  if (fromUrl) return fromUrl;
  try {
    const ls = localStorage.getItem(GROUPING_LS_KEY);
    if (ls && GROUP_BY_OPTS.some((o) => o.k === ls)) return ls;
  } catch { /* ignore */ }
  return 'owner';
}

function matchesSearch(app, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  if (app.name?.toLowerCase().includes(t)) return true;
  if (app.description?.toLowerCase().includes(t)) return true;
  if ((app.entityTypes || []).some((e) => e.toLowerCase().includes(t))) return true;
  return false;
}

function distinctSources(apps) {
  const s = new Set();
  apps.forEach((a) => (a.integrations || []).forEach((integ) => integ.type && s.add(integ.type)));
  return ['*', ...Array.from(s).sort()];
}

function distinctEntityTypes(apps) {
  const s = new Set();
  apps.forEach((a) => (a.entityTypes || []).forEach((e) => s.add(e)));
  return ['*', ...Array.from(s).sort()];
}

function groupApps(apps, groupBy) {
  if (groupBy === 'flat') {
    return [{ key: 'all', title: 'All applications', kind: '', description: '', apps }];
  }
  if (groupBy === 'source') {
    // Every app lands in at least one bucket. Apps with no face that
    // declares sourceKind are collected under "native" (e.g. a bare
    // tool face). Without this fallback, such apps disappear entirely
    // when grouping by source.
    const byKind = new Map();
    const pushOnce = (k, a) => {
      if (!byKind.has(k)) byKind.set(k, []);
      const bucket = byKind.get(k);
      if (!bucket.includes(a)) bucket.push(a);
    };
    apps.forEach((a) => {
      const integrations = a.integrations || [];
      const kinds = integrations.map((integ) => integ.type).filter(Boolean);
      if (kinds.length === 0) {
        pushOnce('native', a);
        return;
      }
      kinds.forEach((k) => pushOnce(k, a));
    });
    return Array.from(byKind.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, list]) => ({ key: k, title: k, kind: 'source', description: '', apps: list }));
  }
  // owner
  const byOwner = new Map();
  apps.forEach((a) => {
    const key = a.ownerGroup?.name || 'ungrouped';
    if (!byOwner.has(key)) byOwner.set(key, { og: a.ownerGroup, apps: [] });
    byOwner.get(key).apps.push(a);
  });
  return Array.from(byOwner.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => ({
      key: k,
      title: v.og?.title || k,
      kind: v.og?.type || '',
      description: '',
      apps: v.apps,
    }));
}

function Chip({ on, onClick, children, count }) {
  return (
    <button type="button" className={`chip${on ? ' on' : ''}`} onClick={onClick}>
      {children}
      {typeof count === 'number' && <span className="c">{count}</span>}
    </button>
  );
}

function FacetGroup({ label, value, options, onChange, labelFor = (k) => k, countFor }) {
  return (
    <div className="facetGroup">
      <span className="lbl">{label}</span>
      {options.map((o) => {
        const k = typeof o === 'string' ? o : o.k;
        const l = typeof o === 'string' ? labelFor(o) : o.l;
        return (
          <Chip key={k} on={value === k} onClick={() => onChange(k)} count={countFor ? countFor(k) : undefined}>
            {l}
          </Chip>
        );
      })}
    </div>
  );
}

export function ApplicationsCatalog() {
  // JOBE-0193: per-route document title.
  useDocumentTitle('Applications');
  const { id } = useParams();
  const navigate = useNavigate();
  const { onOpenChat } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const { apps, err: appsErr } = useApplications();

  const getParam = (k, d = '*') => searchParams.get(k) || d;
  const groupBy     = initialGrouping(searchParams.get('group'));
  const sourceKind  = getParam('source');
  const hasUI       = getParam('ui');
  const entityType  = getParam('ent');
  const rawQ        = searchParams.get('q') || '';

  const [qInput, setQInput] = useState(rawQ);
  const q = useDebounced(qInput, SEARCH_DEBOUNCE_MS);

  // Keep URL state in sync with debounced query (but skip echo when same).
  useEffect(() => {
    if ((searchParams.get('q') || '') === q) return;
    const next = new URLSearchParams(searchParams);
    if (q) next.set('q', q); else next.delete('q');
    setSearchParams(next, { replace: true });
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local input from URL on external changes (back/forward, paste).
  // Skip when the URL value matches our debounced q — that's our own echo.
  useEffect(() => {
    if (rawQ !== q) setQInput(rawQ);
  }, [rawQ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist grouping choice.
  useEffect(() => {
    try { localStorage.setItem(GROUPING_LS_KEY, groupBy); } catch { /* ignore */ }
  }, [groupBy]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === '*') next.delete(k); else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  // Route-scoped / hotkey: focus the search input.
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/') return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const sourceOptions = useMemo(() => distinctSources(apps), [apps]);
  const entityOptions = useMemo(() => distinctEntityTypes(apps), [apps]);

  const filtered = useMemo(() => apps.filter((a) => {
    if (!matchesSearch(a, q)) return false;
    if (sourceKind !== '*' && !(a.integrations || []).some((integ) => integ.type === sourceKind)) return false;
    if (hasUI === 'y' && !a.launchURL) return false;
    if (hasUI === 'n' && a.launchURL) return false;
    if (entityType !== '*' && !(a.entityTypes || []).includes(entityType)) return false;
    return true;
  }), [apps, q, sourceKind, hasUI, entityType]);

  const sections = useMemo(() => groupApps(filtered, groupBy), [filtered, groupBy]);

  // KPIs derived from the unfiltered fetched list.
  const kpis = useMemo(() => {
    const providers = apps.reduce((n, a) => n + (a.integrations || []).filter((i) => i.type === 'provider').length, 0);
    const caps = apps.reduce((n, a) => n + (a.capabilities || []).length, 0);
    const groups = new Set(apps.map((a) => a.ownerGroup?.name).filter(Boolean)).size;
    return [
      { v: apps.length, l: 'Applications' },
      { v: providers,   l: 'Providers' },
      { v: caps,        l: 'Capabilities' },
      { v: groups,      l: 'Groups' },
    ];
  }, [apps]);

  const openDetail = (appId) => navigate(`/applications/${encodeURIComponent(appId)}`);

  return (
    <div className="applications-route">
      <div className="chead">
        <div>
          <div className="kicker">Applications</div>
          <h2>Catalog <em>— what you’ve wired up</em></h2>
        </div>
        <div className="kpis">
          {kpis.map((k) => (
            <div className="k" key={k.l}>
              <div className="v">{k.v}</div>
              <div className="l">{k.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="searchBar">
        <div className="input">
          <span className="glyph">⌕</span>
          <input
            ref={searchRef}
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search applications by name, description, entity type…"
          />
          <kbd>/</kbd>
        </div>
        <div className="result">
          <b>{filtered.length}</b> of {apps.length} applications
        </div>
      </div>

      {appsErr && (
        <div
          role="alert"
          style={{
            fontFamily: 'var(--ff-mono)',
            fontSize: 12,
            color: 'var(--ink-soft)',
            padding: '6px 10px',
            border: '1px dashed var(--line)',
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          — couldn’t load applications ({appsErr}) —
        </div>
      )}

      <div className="facets">
        <FacetGroup
          label="Group by"
          value={groupBy}
          options={GROUP_BY_OPTS}
          onChange={(k) => setParam('group', k)}
        />
        <FacetGroup
          label="Source"
          value={sourceKind}
          options={sourceOptions.map((k) => ({ k, l: k === '*' ? 'All' : k }))}
          onChange={(k) => setParam('source', k)}
        />
        <FacetGroup
          label="Has UI"
          value={hasUI}
          options={UI_OPTS}
          onChange={(k) => setParam('ui', k)}
        />
        <FacetGroup
          label="Entity"
          value={entityType}
          options={entityOptions.map((k) => ({ k, l: k === '*' ? 'All' : k }))}
          onChange={(k) => setParam('ent', k)}
        />
      </div>

      <div className="body">
        {sections.length === 0 && (
          <div style={{ padding: '40px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', fontStyle: 'italic', textAlign: 'center' }}>
            — nothing matches —
          </div>
        )}
        {sections.map((sec) => (
          <section key={sec.key} className="providerSection">
            <div className="phead">
              <div className="pleft">
                <h3>{sec.title}</h3>
                {sec.kind && <span className={`kind${sec.kind === 'external' ? ' external' : ''}`}>{sec.kind}</span>}
              </div>
              <div className="pmeta"><b>{sec.apps.length}</b> applications</div>
            </div>
            <div className="cards">
              {sec.apps.map((a) => <ApplicationCard key={a.name} app={a} onOpen={openDetail} />)}
            </div>
          </section>
        ))}
      </div>

      <div className="legendFoot">
        <span className="item"><span className="dot ok" /> available</span>
        <span className="item"><span className="dot watch" /> needs credentials</span>
        <span className="item"><span className="dot block" /> offline</span>
        <span className="item"><span className="dot pencil" /> installed, no policy</span>
      </div>

      {id && <ApplicationDetail id={id} onOpenChat={onOpenChat} />}
    </div>
  );
}
